import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuthStore } from '@/stores/authStore';

// Layouts - loaded immediately as they wrap all routes
import { MainLayout } from '@/components/layout/MainLayout';
import { AuthLayout } from '@/components/layout/AuthLayout';

// Auth Pages - loaded immediately for fast initial load
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { VerifyOtpPage } from '@/pages/auth/VerifyOtpPage';
import { RoleSelectPage } from '@/pages/auth/RoleSelectPage';

// Lazy-loaded pages for better code splitting
// Dashboard Pages
const FamilyDashboard = lazy(() => import('@/pages/dashboard/FamilyDashboard').then(m => ({ default: m.FamilyDashboard })));
const CaregiverDashboard = lazy(() => import('@/pages/dashboard/CaregiverDashboard').then(m => ({ default: m.CaregiverDashboard })));

// Care Request Pages
const CareRequestListPage = lazy(() => import('@/pages/care-requests/CareRequestListPage').then(m => ({ default: m.CareRequestListPage })));
const CreateCareRequestPage = lazy(() => import('@/pages/care-requests/CreateCareRequestPage').then(m => ({ default: m.CreateCareRequestPage })));
const CareRequestDetailPage = lazy(() => import('@/pages/care-requests/CareRequestDetailPage').then(m => ({ default: m.CareRequestDetailPage })));

// Caregiver Profile Pages
const CaregiverProfilePage = lazy(() => import('@/pages/caregiver/CaregiverProfilePage').then(m => ({ default: m.CaregiverProfilePage })));
const CaregiverProfileSetupPage = lazy(() => import('@/pages/caregiver/CaregiverProfileSetupPage').then(m => ({ default: m.CaregiverProfileSetupPage })));
const AddCertificatePage = lazy(() => import('@/pages/caregiver/AddCertificatePage').then(m => ({ default: m.AddCertificatePage })));
const CaregiverJobsPage = lazy(() => import('@/pages/caregiver/CaregiverJobsPage').then(m => ({ default: m.CaregiverJobsPage })));
const CaregiverJobDetailPage = lazy(() => import('@/pages/caregiver/CaregiverJobDetailPage').then(m => ({ default: m.CaregiverJobDetailPage })));
const CaregiverInvitationsPage = lazy(() => import('@/pages/caregiver/CaregiverInvitationsPage').then(m => ({ default: m.CaregiverInvitationsPage })));
const CaregiverSchedulePage = lazy(() => import('@/pages/caregiver/CaregiverSchedulePage').then(m => ({ default: m.CaregiverSchedulePage })));

// Messages Pages
const MessagesPage = lazy(() => import('@/pages/messages/MessagesPage').then(m => ({ default: m.MessagesPage })));

// Matching Pages
const MatchResultsPage = lazy(() => import('@/pages/matching/MatchResultsPage').then(m => ({ default: m.MatchResultsPage })));
const CaregiverDetailPage = lazy(() => import('@/pages/matching/CaregiverDetailPage').then(m => ({ default: m.CaregiverDetailPage })));

// Contract Pages
const ContractsPage = lazy(() => import('@/pages/contracts').then(m => ({ default: m.ContractsPage })));

// Booking Pages
const FamilyBookingsPage = lazy(() => import('@/pages/bookings').then(m => ({ default: m.FamilyBookingsPage })));

// Application Pages
const FamilyApplicationsPage = lazy(() => import('@/pages/applications').then(m => ({ default: m.FamilyApplicationsPage })));

// Legal Pages
const TermsOfServicePage = lazy(() => import('@/pages/legal').then(m => ({ default: m.TermsOfServicePage })));
const PrivacyPolicyPage = lazy(() => import('@/pages/legal').then(m => ({ default: m.PrivacyPolicyPage })));
const DeleteAccountPage = lazy(() => import('@/pages/legal').then(m => ({ default: m.DeleteAccountPage })));

// Admin Pages
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const UsersManagementPage = lazy(() => import('@/pages/admin/UsersManagementPage'));
const VerificationsPage = lazy(() => import('@/pages/admin/VerificationsPage'));
const FinancialReportsPage = lazy(() => import('@/pages/admin/FinancialReportsPage'));

// Page loading spinner
function PageLoader() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
      <CircularProgress />
    </Box>
  );
}

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: string[] }) {
  const { isAuthenticated, user, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If user has no role yet, redirect to role selection
  if (user && (!user.role || user.status === 'pending_role')) {
    return <Navigate to="/role-select" replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    // Redirect to the correct dashboard based on role
    const redirectPath = user.role === 'admin' ? '/admin' : user.role === 'caregiver' ? '/caregiver' : '/family';
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
}

// Guest Route Component (redirect if already authenticated)
function GuestRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthenticated && user) {
    // If user has no role yet, redirect to role selection
    if (!user.role || user.status === 'pending_role') {
      return <Navigate to="/role-select" replace />;
    }
    const redirectPath = user.role === 'admin' ? '/admin' : user.role === 'caregiver' ? '/caregiver' : '/family';
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <>
      <Routes>
        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route
            path="/login"
            element={
              <GuestRoute>
                <LoginPage />
              </GuestRoute>
            }
          />
          <Route
            path="/register"
            element={
              <GuestRoute>
                <RegisterPage />
              </GuestRoute>
            }
          />
          <Route
            path="/verify-otp"
            element={
              <GuestRoute>
                <VerifyOtpPage />
              </GuestRoute>
            }
          />
          <Route
            path="/role-select"
            element={<RoleSelectPage />}
          />
        </Route>

        {/* Protected Routes */}
        <Route element={<MainLayout />}>
          {/* Family Routes */}
          <Route
            path="/family"
            element={
              <ProtectedRoute allowedRoles={['family']}>
                <Suspense fallback={<PageLoader />}>
                  <FamilyDashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Care Request Routes */}
          <Route
            path="/care-requests"
            element={
              <ProtectedRoute allowedRoles={['family']}>
                <Suspense fallback={<PageLoader />}>
                  <CareRequestListPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/care-requests/new"
            element={
              <ProtectedRoute allowedRoles={['family']}>
                <Suspense fallback={<PageLoader />}>
                  <CreateCareRequestPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/care-requests/:id"
            element={
              <ProtectedRoute allowedRoles={['family']}>
                <Suspense fallback={<PageLoader />}>
                  <CareRequestDetailPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Family Bookings Route */}
          <Route
            path="/bookings"
            element={
              <ProtectedRoute allowedRoles={['family']}>
                <Suspense fallback={<PageLoader />}>
                  <FamilyBookingsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Family Applications Route */}
          <Route
            path="/applications"
            element={
              <ProtectedRoute allowedRoles={['family']}>
                <Suspense fallback={<PageLoader />}>
                  <FamilyApplicationsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Matching Routes */}
          <Route
            path="/matching"
            element={
              <ProtectedRoute allowedRoles={['family']}>
                <Suspense fallback={<PageLoader />}>
                  <MatchResultsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregivers/:caregiverId"
            element={
              <ProtectedRoute allowedRoles={['family']}>
                <Suspense fallback={<PageLoader />}>
                  <CaregiverDetailPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Caregiver Routes */}
          <Route
            path="/caregiver"
            element={
              <ProtectedRoute allowedRoles={['caregiver']}>
                <Suspense fallback={<PageLoader />}>
                  <CaregiverDashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver/profile"
            element={
              <ProtectedRoute allowedRoles={['caregiver']}>
                <Suspense fallback={<PageLoader />}>
                  <CaregiverProfilePage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver/profile/setup"
            element={
              <ProtectedRoute allowedRoles={['caregiver']}>
                <Suspense fallback={<PageLoader />}>
                  <CaregiverProfileSetupPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver/profile/edit"
            element={
              <ProtectedRoute allowedRoles={['caregiver']}>
                <Suspense fallback={<PageLoader />}>
                  <CaregiverProfileSetupPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver/certificates/add"
            element={
              <ProtectedRoute allowedRoles={['caregiver']}>
                <Suspense fallback={<PageLoader />}>
                  <AddCertificatePage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver/jobs"
            element={
              <ProtectedRoute allowedRoles={['caregiver']}>
                <Suspense fallback={<PageLoader />}>
                  <CaregiverJobsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver/jobs/:careRequestId"
            element={
              <ProtectedRoute allowedRoles={['caregiver']}>
                <Suspense fallback={<PageLoader />}>
                  <CaregiverJobDetailPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver/invitations"
            element={
              <ProtectedRoute allowedRoles={['caregiver']}>
                <Suspense fallback={<PageLoader />}>
                  <CaregiverInvitationsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver/schedule"
            element={
              <ProtectedRoute allowedRoles={['caregiver']}>
                <Suspense fallback={<PageLoader />}>
                  <CaregiverSchedulePage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver/contracts"
            element={
              <ProtectedRoute allowedRoles={['caregiver']}>
                <Suspense fallback={<PageLoader />}>
                  <ContractsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Contract Routes - accessible by both family and caregiver */}
          <Route
            path="/contracts"
            element={
              <ProtectedRoute allowedRoles={['family', 'caregiver']}>
                <Suspense fallback={<PageLoader />}>
                  <ContractsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Messages Route - accessible by both roles */}
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <MessagesPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Suspense fallback={<PageLoader />}>
                  <AdminDashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Suspense fallback={<PageLoader />}>
                  <UsersManagementPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/verifications"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Suspense fallback={<PageLoader />}>
                  <VerificationsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Suspense fallback={<PageLoader />}>
                  <FinancialReportsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Legal Pages - accessible by all */}
        <Route
          path="/terms"
          element={
            <Suspense fallback={<PageLoader />}>
              <TermsOfServicePage />
            </Suspense>
          }
        />
        <Route
          path="/privacy"
          element={
            <Suspense fallback={<PageLoader />}>
              <PrivacyPolicyPage />
            </Suspense>
          }
        />
        <Route
          path="/delete-account"
          element={
            <Suspense fallback={<PageLoader />}>
              <DeleteAccountPage />
            </Suspense>
          }
        />

        {/* Redirect root to appropriate dashboard */}
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* 404 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default App;
